import { DayOfWeek, WorkingHours } from '@vacationtracker/shared/types/work-week'
import { IGetLocationGeneral } from '../../../../../../types/locations'
import { IPlaceHolidays } from '../../../../../../util/location-facts'

/** The General tab's fields, all of which ride a full LOCATION_UPDATED. */
export type TLocationGeneralPatch = Partial<
  Pick<IGetLocationGeneral, 'name' | 'timezone' | 'firstDayOfWeek' | 'resetQuotas' | 'isDefault' | 'yearStartMonth' | 'yearStartDay' | 'countryCode' | 'subdivisionCode'>
>

/**
 * A full LOCATION_UPDATED carrying one changed field.
 *
 * There is no per-field location event, so an inline save has to re-send everything. This is an
 * explicit whitelist rather than a spread of the location for two reasons, both of which bite:
 *
 * - locationUpdatedSchema has no `.unknown()` and put-event validates with neither allowUnknown nor
 *   stripUnknown, so a single undeclared key is a 400. IGetLocationGeneral carries
 *   rolloverNeverExpireDays, holidays, leavePolicies and timestamp, none of which the schema takes.
 * - The handler writes most keys unconditionally, and Joi supplies defaults for the ones it does
 *   not receive - so an omitted key is not a skipped key, it is a silent overwrite.
 */
/** `userIds` is read fresh at save time rather than taken off `location` - see the `users` note below. */
export function buildLocationUpdatedPayload(location: IGetLocationGeneral, patch: TLocationGeneralPatch, userIds: string[]) {
  const next = { ...location, ...patch }

  // A subdivision only means anything under the country it belongs to, and the schema rejects a
  // mismatched pair. So moving the country without naming a new subdivision drops the old
  // one rather than sending a combination the backend will refuse.
  const countryChanged = 'countryCode' in patch && (patch.countryCode ?? null) !== (location.countryCode ?? null)
  const subdivisionCode = countryChanged && patch.subdivisionCode === undefined ? null : (next.subdivisionCode ?? null)

  return {
    eventType: 'LOCATION_UPDATED' as const,
    eventGroup: 'LOCATION' as const,
    locationId: location.id,

    name: next.name,
    timezone: next.timezone,
    firstDayOfWeek: next.firstDayOfWeek,
    resetQuotas: next.resetQuotas,

    // Joi defaults this to false and the handler writes it, so omitting it leaves the
    // company with no default location at all, with no error.
    isDefault: next.isDefault,

    // The same trap, and easier to miss: Joi defaults both to 1 whenever resetQuotas is not
    // FISCAL_YEAR, so omitting them on a USER_YEAR location destroys the fiscal-year start it still
    // holds and would use again if it were switched back.
    yearStartMonth: next.yearStartMonth,
    yearStartDay: next.yearStartDay,

    // Required, and the handler diffs it against the location's current members: anyone missing is
    // moved to the default location. Always the full list, never [] - and read at save time, because
    // the rendered one lags every move made on the Users tab.
    users: userIds,

    // The only pair the handler skips when undefined, which is why a cleared Select has to become an
    // explicit null - JSON.stringify drops undefined and the old value would survive. Clearing the
    // country clears the subdivision too: the schema rejects a subdivision with no country.
    countryCode: next.countryCode ?? null,
    subdivisionCode: next.countryCode ? subdivisionCode : null,
  }
}

export interface IWorkWeekChange {
  workWeek: number[]
  hoursPerDay?: number | null
  /** Whether to overwrite the per-user work weeks of people who have their own. */
  cancelUserSettings: boolean
}

export function buildLocationWorkWeekPayload(locationId: string, next: IWorkWeekChange) {
  // A copy: workWeekAsText and sortWorkWeekFromDay both sort in place, and one of them sorts a
  // module-level constant.
  const days = [...next.workWeek].sort((a, b) => a - b) as DayOfWeek[]
  const hours = next.hoursPerDay

  return {
    eventType: 'LOCATION_WORK_WEEK_UPDATED' as const,
    eventGroup: 'LOCATION' as const,
    locationId,
    days,
    // TODO workday - one average number until there is UI for custom work hours per day.
    workHours: hours ? (days.map((day) => [day, hours]) as WorkingHours) : null,
    cancelUserSettings: next.cancelUserSettings,
  }
}

export interface IHolidaysImportedParams {
  locationId: string
  /** The place being imported for, passed in rather than read off the location: on a country change
   *  the caller has the new one and the stored location still has the old. */
  place: { countryCode: string; subdivisionCode?: string | null }
  placeHolidays: IPlaceHolidays
  locale: string
}

export function buildHolidaysImportedPayload(params: IHolidaysImportedParams) {
  const { locationId, place, placeHolidays, locale } = params

  return {
    eventType: 'LOCATION_HOLIDAYS_IMPORTED' as const,
    eventGroup: 'LOCATION' as const,
    locationId,
    holidays: placeHolidays.holidays,
    year: new Date().getFullYear(),
    country: place.countryCode,
    // Joi.string().optional() with no .allow(null), so null is a 400 - it has to be absent.
    state: place.subdivisionCode ?? undefined,
    locale,
    // This pair is what auto-import means: the January cron re-imports anything stamped with it.
    recurring: true,
    holidayLib: 'DATE_HOLIDAYS' as const,
    // A place change must not leave the old place's imports behind; hand-added holidays are still kept.
    replaceImported: true,
  }
}
